export const jsonToTsvContent = {
    title: "JSON to TSV Converter",
    description: "Convert JSON data to TSV (Tab-Separated Values) format. Free online JSON to TSV converter.",
    content: (
        <>
            <h1 className="text-3xl font-bold text-gray-900 mb-6">JSON to TSV Converter</h1>
            <p className="text-gray-700 mb-6 leading-relaxed">Convert JSON (JavaScript Object Notation) data to TSV (Tab-Separated Values) format with our free online converter. Perfect for data migration and format conversion.</p>

            <h2 className="text-2xl font-semibold text-gray-900 mb-4">What is JSON to TSV Conversion?</h2>
            <p className="text-gray-700 mb-4">JSON to TSV conversion transforms:</p>
            <ul className="list-disc pl-6 mb-6 space-y-2">
                <li><span className="font-semibold text-gray-900">JSON</span>: Structured data format with objects/arrays</li>
                <li><span className="font-semibold text-gray-900">TSV</span>: Tab-separated values with rows/columns</li>
                <li><span className="font-semibold text-gray-900">Structure</span>: Hierarchical to flat table format</li>
                <li><span className="font-semibold text-gray-900">Use Case</span>: Data exchange and spreadsheet import</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mb-4">How It Works</h2>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Input Processing</h3>
            <ol className="list-decimal pl-6 mb-6 space-y-2">
                <li><span className="font-semibold text-gray-900">Parse JSON</span>: Validate and parse JSON structure</li>
                <li><span className="font-semibold text-gray-900">Extract Data</span>: Convert nested objects to flat structure</li>
                <li><span className="font-semibold text-gray-900">Handle Arrays</span>: Process JSON arrays to rows</li>
                <li><span className="font-semibold text-gray-900">Type Detection</span>: Automatic data type identification</li>
            </ol>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Output Generation</h3>
            <ol className="list-decimal pl-6 mb-6 space-y-2">
                <li><span className="font-semibold text-gray-900">Create Headers</span>: Extract keys as column headers</li>
                <li><span className="font-semibold text-gray-900">Format Rows</span>: Convert each object to TSV row</li>
                <li><span className="font-semibold text-gray-900">Tab Separation</span>: Use tab characters as delimiters</li>
                <li><span className="font-semibold text-gray-900">Handle Escaping</span>: Escape tabs and newlines in data</li>
            </ol>

            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Supported JSON Formats</h2>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Object Format</h3>
            <pre className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-6 overflow-x-auto"><code className="text-sm text-gray-800">{`{
  "name": "John",
  "age": 30,
  "city": "New York"
}`}</code></pre>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Array Format</h3>
            <pre className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-6 overflow-x-auto"><code className="text-sm text-gray-800">{`[
  {"name": "John", "age": 30},
  {"name": "Jane", "age": 25}
]`}</code></pre>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Nested Structure</h3>
            <pre className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-6 overflow-x-auto"><code className="text-sm text-gray-800">{`{
  "user": {
    "name": "John",
    "contact": {
      "email": "john@example.com",
      "phone": "123-456-7890"
    }
  }
}`}</code></pre>

            <h2 className="text-2xl font-semibold text-gray-900 mb-4">TSV Output Format</h2>
            <p className="text-gray-700 mb-4">The converted TSV format includes:</p>
            <ul className="list-disc pl-6 mb-6 space-y-2">
                <li><span className="font-semibold text-gray-900">Tab Separators</span>: Tab characters between values</li>
                <li><span className="font-semibold text-gray-900">Line Breaks</span>: New lines for each row</li>
                <li><span className="font-semibold text-gray-900">Headers</span>: First row with column names</li>
                <li><span className="font-semibold text-gray-900">Escaping</span>: Proper handling of special characters</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Basic Structure</h3>
            <pre className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-6 overflow-x-auto"><code className="text-sm text-gray-800">{`name	age	city
John	30	New York
Jane	25	Los Angeles`}</code></pre>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Escaped Characters</h3>
            <ul className="list-disc pl-6 mb-6 space-y-2">
                <li><span className="font-semibold text-gray-900">Tab Characters</span>: Represented as \\t</li>
                <li><span className="font-semibold text-gray-900">Newlines</span>: Represented as \\n</li>
                <li><span className="font-semibold text-gray-900">Special Characters</span>: Properly escaped</li>
                <li><span className="font-semibold text-gray-900">Unicode Support</span>: Full UTF-8 support</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Use Cases</h2>
            <ul className="list-disc pl-6 mb-6 space-y-2">
                <li><span className="font-semibold text-gray-900">Data Migration</span>: Convert between systems</li>
                <li><span className="font-semibold text-gray-900">Spreadsheet Import</span>: Excel, Google Sheets import</li>
                <li><span className="font-semibold text-gray-900">Database Export</span>: Database backup/export</li>
                <li><span className="font-semibold text-gray-900">API Integration</span>: Web service data exchange</li>
                <li><span className="font-semibold text-gray-900">Data Analysis</span>: Statistical analysis preparation</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Advanced Features</h2>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Data Type Handling</h3>
            <ul className="list-disc pl-6 mb-6 space-y-2">
                <li><span className="font-semibold text-gray-900">Strings</span>: Preserved with quotes</li>
                <li><span className="font-semibold text-gray-900">Numbers</span>: Unquoted numeric values</li>
                <li><span className="font-semibold text-gray-900">Booleans</span>: Converted to true/false</li>
                <li><span className="font-semibold text-gray-900">Null Values</span>: Empty fields for null</li>
                <li><span className="font-semibold text-gray-900">Dates</span>: ISO format preservation</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Array Processing</h3>
            <ul className="list-disc pl-6 mb-6 space-y-2">
                <li><span className="font-semibold text-gray-900">Objects</span>: Each array element becomes a row</li>
                <li><span className="font-semibold text-gray-900">Mixed Arrays</span>: Handles uniform and non-uniform arrays</li>
                <li><span className="font-semibold text-gray-900">Nested Arrays</span>: Flattens nested structures</li>
                <li><span className="font-semibold text-gray-900">Empty Arrays</span>: Handles empty JSON arrays</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Customization Options</h3>
            <ul className="list-disc pl-6 mb-6 space-y-2">
                <li><span className="font-semibold text-gray-900">Delimiter Selection</span>: Tab, comma, semicolon</li>
                <li><span className="font-semibold text-gray-900">Header Inclusion</span>: Include/exclude column headers</li>
                <li><span className="font-semibold text-gray-900">Line Endings</span>: Windows (CRLF) or Unix (LF)</li>
                <li><span className="font-semibold text-gray-900">Encoding Selection</span>: UTF-8, UTF-16, ASCII</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Data Validation</h2>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Input Validation</h3>
            <ul className="list-disc pl-6 mb-6 space-y-2">
                <li><span className="font-semibold text-gray-900">JSON Syntax</span>: Check for valid JSON format</li>
                <li><span className="font-semibold text-gray-900">Error Reporting</span>: Detailed error messages</li>
                <li><span className="font-semibold text-gray-900">Recovery Suggestions</span>: Fix common JSON issues</li>
                <li><span className="font-semibold text-gray-900">Character Encoding</span>: Handle various encodings</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Output Validation</h3>
            <ul className="list-disc pl-6 mb-6 space-y-2">
                <li><span className="font-semibold text-gray-900">Tab Separation</span>: Ensure proper tab delimiters</li>
                <li><span className="font-semibold text-gray-900">Line Consistency</span>: Uniform row formatting</li>
                <li><span className="font-semibold text-gray-900">Character Escaping</span>: Proper special character handling</li>
                <li><span className="font-semibold text-gray-900">File Size</span>: Large file support</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Performance Considerations</h2>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Large Files</h3>
            <ul className="list-disc pl-6 mb-6 space-y-2">
                <li><span className="font-semibold text-gray-900">Streaming</span>: Process large files efficiently</li>
                <li><span className="font-semibold text-gray-900">Memory Usage</span>: Optimize for big datasets</li>
                <li><span className="font-semibold text-gray-900">Progress Tracking</span>: Show conversion progress</li>
                <li><span className="font-semibold text-gray-900">Error Recovery</span>: Handle partial failures</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Memory Optimization</h3>
            <ul className="list-disc pl-6 mb-6 space-y-2">
                <li><span className="font-semibold text-gray-900">Chunk Processing</span>: Process data in chunks</li>
                <li><span className="font-semibold text-gray-900">Garbage Collection</span>: Proper memory cleanup</li>
                <li><span className="font-semibold text-gray-900">Buffer Management</span>: Efficient I/O operations</li>
                <li><span className="font-semibold text-gray-900">Async Processing</span>: Non-blocking operations</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Best Practices</h2>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Data Preparation</h3>
            <ul className="list-disc pl-6 mb-6 space-y-2">
                <li><span className="font-semibold text-gray-900">Clean JSON</span>: Remove unnecessary whitespace</li>
                <li><span className="font-semibold text-gray-900">Validate Structure</span>: Ensure consistent format</li>
                <li><span className="font-semibold text-gray-900">Handle Nulls</span>: Decide on null value treatment</li>
                <li><span className="font-semibold text-gray-900">Type Consistency</span>: Uniform data types</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Output Optimization</h3>
            <ul className="list-disc pl-6 mb-6 space-y-2">
                <li><span className="font-semibold text-gray-900">Proper Escaping</span>: Handle special characters</li>
                <li><span className="font-semibold text-gray-900">Consistent Formatting</span>: Uniform row structure</li>
                <li><span className="font-semibold text-gray-900">Header Management</span>: Clear column names</li>
                <li><span className="font-semibold text-gray-900">File Encoding</span>: Appropriate character encoding</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Common Issues and Solutions</h2>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Nested Objects</h3>
            <ul className="list-disc pl-6 mb-6 space-y-2">
                <li><span className="font-semibold text-gray-900">Problem</span>: How to flatten nested JSON?</li>
                <li><span className="font-semibold text-gray-900">Solution</span>: Use dot notation or custom flattening</li>
                <li><span className="font-semibold text-gray-900">Example</span>: user.name becomes user.name</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Arrays of Objects</h3>
            <ul className="list-disc pl-6 mb-6 space-y-2">
                <li><span className="font-semibold text-gray-900">Problem</span>: How to handle mixed content?</li>
                <li><span className="font-semibold text-gray-900">Solution</span>: Create consistent column structure</li>
                <li><span className="font-semibold text-gray-900">Example</span>: Use separate columns for different data types</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Special Characters</h3>
            <ul className="list-disc pl-6 mb-6 space-y-2">
                <li><span className="font-semibold text-gray-900">Problem</span>: Tabs and newlines in data?</li>
                <li><span className="font-semibold text-gray-900">Solution</span>: Proper escaping with quotes</li>
                <li><span className="font-semibold text-gray-900">Example</span>: "Value with\\ttab" becomes "Value with\\ttab"</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Integration Examples</h2>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Database Export</h3>
            <pre className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-6 overflow-x-auto"><code className="text-sm text-gray-800">{`-- Export data as TSV for database import
COPY users FROM '/path/to/data.tsv' DELIMITER E' CSV HEADER;`}</code></pre>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Web API</h3>
            <pre className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-6 overflow-x-auto"><code className="text-sm text-gray-800">{`// Convert JSON response to TSV format
const jsonData = await fetch('/api/data');
const tsvData = convertJsonToTsv(jsonData);`}</code></pre>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Spreadsheet Import</h3>
            <ul className="list-disc pl-6 mb-6 space-y-2">
                <li><span className="font-semibold text-gray-900">Excel</span>: Direct TSV file import</li>
                <li><span className="font-semibold text-gray-900">Google Sheets</span>: Import from URL or file</li>
                <li><span className="font-semibold text-gray-900">LibreOffice</span>: Native TSV support</li>
                <li><span className="font-semibold text-gray-900">Numbers</span>: Mac spreadsheet app support</li>
            </ul>

            <p className="text-gray-700 leading-relaxed">Use our JSON to TSV converter for reliable data format conversion with proper handling of complex JSON structures.</p>
        </>
    ),
    faqs: [
        {
            question: "What's the difference between JSON and TSV?",
            answer: "JSON is a hierarchical data format with objects and arrays, while TSV is a flat table format with rows and columns separated by tab characters. JSON preserves structure, TSV is better for spreadsheet applications."
        },
        {
            question: "How are nested JSON objects handled?",
            answer: "Nested objects are flattened using dot notation (e.g., user.name becomes 'user.name') or converted to separate columns. Some tools offer customizable flattening options."
        },
        {
            question: "Can TSV handle special characters?",
            answer: "Yes, TSV can handle special characters through proper escaping. Tabs within data are escaped as \\t, newlines as \\n, and quotes are used to preserve special characters."
        },
        {
            question: "What about JSON arrays?",
            answer: "JSON arrays are typically converted to TSV rows, with each array element becoming a separate row. Objects within arrays are flattened to columns using consistent keys."
        },
        {
            question: "Is TSV better than CSV?",
            answer: "TSV is better when your data contains commas, as tabs don't conflict with comma-separated values. However, CSV is more widely supported by legacy systems."
        },
        {
            question: "What is the difference between TSV and CSV?",
            answer: "TSV uses tabs to separate values, while CSV uses commas. TSV is better for data with commas, while CSV is more widely supported."
        }
    ],
    relatedTools: [
        { name: "JSON to CSV Converter", url: "/tools/json-to-csv", description: "Convert JSON data to CSV format" },
        { name: "CSV to TSV Converter", url: "/tools/csv-to-tsv", description: "Convert CSV data to TSV format" },
        { name: "JSON Formatter", url: "/tools/json-formatter", description: "Format and beautify JSON data" },
        { name: "Base64 Encoder", url: "/tools/base64", description: "Encode and decode Base64 strings for data transmission" },
        { name: "Text Editor", url: "/tools/text-editor", description: "Edit and format TSV data after conversion" }
    ]
} as const;
